import { Material, Object3D, Scene } from "three";

import type { Engine } from "./Engine";

const MATERIAL_WRAPPED = Symbol.for(
  "sugar.sceneMutationObserver.materialWrapped",
);
const ENGINE_DIRTY_FLAG = Symbol.for(
  "sugar.sceneMutationObserver.engineDirty",
);

/** `updateMatrix` sonrası bu eşiğin altındaki farklar gürültü sayılır (scene-mutated tetiklenmez). */
const MATRIX_CHANGE_EPSILON = 1e-4;

const MATERIAL_WATCHED_FIELDS: readonly string[] = [
  "color",
  "emissive",
  "opacity",
  "transparent",
  "visible",
  "side",
  "blending",
  "map",
  "emissiveMap",
  "normalMap",
  "roughnessMap",
  "metalnessMap",
  "aoMap",
  "alphaMap",
  "envMap",
  "roughness",
  "metalness",
];

type DirtyHost = { [ENGINE_DIRTY_FLAG]?: boolean };

type MaybeWrapped = Material & { [MATERIAL_WRAPPED]?: boolean };

/**
 * three.js Object3D / Material prototype'larına tek seferlik monkey-patch
 * uygulayarak sahne grafı, transform ve material mutasyonlarını generic
 * olarak yakalar. Gözlemlenen her Engine'de `pendingDirty` bayrağını set eder;
 * Engine frame başında `consumeDirty()` çağırıp `scene-mutated` dispatch eder.
 *
 * Mutasyon noktalarına tek tek kod eklemek gerekmez — three.js'in kendi
 * primitif'lerinden çekilir. Material için three.js uniform change event'i
 * vermediği için yaygın setter'lar instance başına sarılır.
 */
export class SceneMutationObserver {
  private static _patched = false;
  private static _scenes = new Map<Scene, Engine>();
  private static _matrixCache = new WeakMap<Object3D, Float32Array>();

  static observe(scene: Scene, engine: Engine): void {
    SceneMutationObserver._ensurePatched();
    SceneMutationObserver._scenes.set(scene, engine);
    scene.traverse((o) => SceneMutationObserver._trackObject(o));
  }

  static unobserve(scene: Scene): void {
    SceneMutationObserver._scenes.delete(scene);
  }

  static consumeDirty(engine: Engine): boolean {
    const host = engine as unknown as DirtyHost;
    if (host[ENGINE_DIRTY_FLAG]) {
      host[ENGINE_DIRTY_FLAG] = false;
      return true;
    }
    return false;
  }

  private static _markDirtyForObject(obj: Object3D): void {
    let root: Object3D = obj;
    while (root.parent) root = root.parent;
    const engine = SceneMutationObserver._scenes.get(root as Scene);
    if (!engine) return;
    (engine as unknown as DirtyHost)[ENGINE_DIRTY_FLAG] = true;
  }

  private static _markAllDirty(): void {
    for (const engine of SceneMutationObserver._scenes.values()) {
      (engine as unknown as DirtyHost)[ENGINE_DIRTY_FLAG] = true;
    }
  }

  private static _trackSubtree(root: Object3D): void {
    root.traverse((o) => SceneMutationObserver._trackObject(o));
  }

  private static _trackObject(o: Object3D): void {
    if (!SceneMutationObserver._matrixCache.has(o)) {
      const snap = new Float32Array(16);
      snap.set(o.matrix.elements);
      SceneMutationObserver._matrixCache.set(o, snap);
    }
    const mat = (o as unknown as { material?: Material | Material[] }).material;
    if (!mat) return;
    if (Array.isArray(mat)) {
      for (const m of mat) SceneMutationObserver._wrapMaterial(m);
    } else {
      SceneMutationObserver._wrapMaterial(mat);
    }
  }

  private static _wrapMaterial(m: Material | undefined): void {
    if (!m) return;
    const tagged = m as MaybeWrapped;
    if (tagged[MATERIAL_WRAPPED]) return;
    tagged[MATERIAL_WRAPPED] = true;

    for (const key of MATERIAL_WATCHED_FIELDS) {
      SceneMutationObserver._wrapMaterialField(m, key);
    }
  }

  private static _wrapMaterialField(m: Material, key: string): void {
    let descriptor: PropertyDescriptor | undefined;
    let target: object | null = m;
    while (target) {
      descriptor = Object.getOwnPropertyDescriptor(target, key);
      if (descriptor) break;
      target = Object.getPrototypeOf(target);
    }

    let backing: unknown = (m as unknown as Record<string, unknown>)[key];
    const hasAccessor = !!descriptor && (!!descriptor.get || !!descriptor.set);

    try {
      Object.defineProperty(m, key, {
        configurable: true,
        enumerable: descriptor?.enumerable ?? true,
        get(): unknown {
          if (hasAccessor && descriptor?.get) {
            return descriptor.get.call(this);
          }
          return backing;
        },
        set(value: unknown): void {
          if (hasAccessor && descriptor?.set) {
            descriptor.set.call(this, value);
          } else {
            backing = value;
          }
          SceneMutationObserver._markAllDirty();
        },
      });
    } catch {
      // Bazı özellikler frozen/non-configurable olabilir; sessizce atla.
    }
  }

  private static _ensurePatched(): void {
    if (SceneMutationObserver._patched) return;
    SceneMutationObserver._patched = true;

    SceneMutationObserver._patchObject3DGraph();
    SceneMutationObserver._patchObject3DTransform();
    SceneMutationObserver._patchMaterialNeedsUpdate();
  }

  private static _patchObject3DGraph(): void {
    const proto = Object3D.prototype;

    const origAdd = proto.add;
    proto.add = function (this: Object3D, ...args: Object3D[]): Object3D {
      const ret = origAdd.apply(this, args);
      for (const child of args) {
        SceneMutationObserver._trackSubtree(child);
      }
      SceneMutationObserver._markDirtyForObject(this);
      return ret;
    };

    const origRemove = proto.remove;
    proto.remove = function (this: Object3D, ...args: Object3D[]): Object3D {
      SceneMutationObserver._markDirtyForObject(this);
      return origRemove.apply(this, args);
    };

    const origClear = proto.clear;
    proto.clear = function (this: Object3D): Object3D {
      SceneMutationObserver._markDirtyForObject(this);
      return origClear.apply(this);
    };
  }

  private static _patchObject3DTransform(): void {
    const proto = Object3D.prototype;
    const origUpdateMatrix = proto.updateMatrix;

    proto.updateMatrix = function (this: Object3D): void {
      origUpdateMatrix.apply(this);

      const elems = this.matrix.elements;
      const cache = SceneMutationObserver._matrixCache.get(this);
      if (!cache) {
        const snap = new Float32Array(16);
        snap.set(elems);
        SceneMutationObserver._matrixCache.set(this, snap);
        return;
      }

      let changed = false;
      for (let i = 0; i < 16; i++) {
        if (Math.abs(cache[i] - elems[i]) > MATRIX_CHANGE_EPSILON) {
          changed = true;
          break;
        }
      }
      if (changed) {
        cache.set(elems);
        SceneMutationObserver._markDirtyForObject(this);
      }

    };
  }

  private static _patchMaterialNeedsUpdate(): void {
    const descriptor = Object.getOwnPropertyDescriptor(
      Material.prototype,
      "needsUpdate",
    );
    if (!descriptor?.set) return;

    const origSetter = descriptor.set;
    Object.defineProperty(Material.prototype, "needsUpdate", {
      configurable: true,
      enumerable: descriptor.enumerable ?? false,
      get: descriptor.get,
      set(value: boolean): void {
        origSetter.call(this, value);
        if (value) SceneMutationObserver._markAllDirty();
      },
    });
  }
}
